import { useCallback, memo } from 'react';
import { cn, Cross } from '@divlab/divanui';

import * as ModalWindows from '@Stores/ModalWindows';
import ModalMain from '@Modals/_components/ModalMain';
import useModalWindowTranslation from '@Queries/useModalWindowTranslation';
import styles from './InfoModal.module.css';

import type { FC, MouseEvent } from 'react';
import type { ModalMainProps } from '@Modals/_components/ModalMain';

type Variant =
  | 'unknown-error'
  | 'order-or-product-not-found-error'
  | 'enter-link-to-product-error'
  | 'product-not-found-error'
  | 'message-was-sent'
  | 'message-was-sent-waiting'
  | 'operator-contact-you'
  | 'manager-callback'
  | 'we-callback'
  | 'notify-about-receipt'
  | 'email-subscribe'
  | 'specialist-call-30-min'
  | 'in-develop'
  | 'consent-publication-photos'
  | 'success-email-confirmed'
  | 'success-subscription'
  | 'profile-data-changed'
  | 'application-sent';

export interface InfoModalData {
  view?: 'success' | 'error';
  variant: Variant;
  needToCloseAll?: boolean;
  onClose?: (e: MouseEvent) => void;
}

export type InfoModalProps = ModalMainProps<InfoModalData>;

interface TextInfo {
  title: string;
  description: string;
}

// NOTE: Здесь специально представлен текст только на дефолтном языке стран,
// так как в случае ошибки мы не можем гарантировать работоспособность механизма локализации
// и будет надежнее иметь именно статичную информацию об ошибке без зависимости от запросов
const defaultTextInfo: TextInfo = {
  title: 'Произошла ошибка',
  description: 'Пожалуйста, повторите попытку позже.',
};

const InfoModal: FC<InfoModalProps> = (props) => {
  const { modal, onClose, ...restProps } = props;
  const { view, variant, needToCloseAll = true } = modal.data;
  const { t, isPending } = useModalWindowTranslation({ modalId: modal.id });

  const handleClose = useCallback(
    (e: MouseEvent) => {
      // Если в модальное окно передан флаг needToCloseAll
      // после закрытия мы сбрасываем всю очередь модальных окон, которая могла возникнуть
      if (needToCloseAll) {
        ModalWindows.closeAll();
      } else if (onClose) onClose(e);

      if (modal.data.onClose) modal.data.onClose(e);
    },
    [needToCloseAll, modal.data, onClose],
  );

  if (isPending) return null;

  const errorInfo = variant === 'unknown-error' ? defaultTextInfo : null;
  const title = errorInfo ? errorInfo.title : t(`${variant}.title`);
  const description = errorInfo ? errorInfo.description : t(`${variant}.description`);
  const text = errorInfo ? undefined : t(`${variant}.text`);

  return (
    <ModalMain {...restProps} view={view} modal={modal} onClose={handleClose}>
      <div hidden data-testid={variant} />
      <div className={cn(styles.container, { [styles.neutral]: !view })}>
        <Cross className={styles.iconClose} onClick={handleClose} />
        <div className={styles.content}>
          <div
            className={cn(styles.icon, {
              [styles.success]: view === 'success',
              [styles.error]: view === 'error',
            })}
          />
          <div className={styles.title}>{title}</div>
          <div className={styles.description}>{description}</div>
          {text && <div className={styles.text}>{text}</div>}
        </div>
      </div>
    </ModalMain>
  );
};

export default memo(InfoModal);
